import { useState } from "react";
import { Shield, Cookie, FileText, Download, Copyright, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PEC = "legendteamvillage@pec.it";

const Section = ({ n, title, children }) => (
  <section className="mb-8">
    <h2 className="font-display font-bold uppercase text-xl md:text-2xl mb-3 flex items-start gap-2">
      {n && <span className="text-legend-red">{n}.</span>} {title}
    </h2>
    <div className="space-y-3 text-slate-700 dark:text-slate-300 leading-relaxed text-[0.95rem]">{children}</div>
  </section>
);

export default function Privacy() {
  const [prefs, setPrefs] = useState({ necessari: true, statistici: false, marketing: false });

  const save = () => {
    localStorage.setItem("legend_cookie_prefs", JSON.stringify(prefs));
    toast.success("Preferenze cookie salvate.");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="privacy-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield size={26} className="text-legend-red" />
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl leading-none">Note Legali & Privacy</h1>
      </div>
      <p className="text-slate-500 font-mono text-sm mb-6">Documento ufficiale — Legend Magazine</p>

      <a href="/legal/note-legali-privacy.pdf" download target="_blank" rel="noreferrer" data-testid="download-legal-pdf" className="inline-flex items-center gap-2 bg-legend-ink text-white font-bold uppercase tracking-wide text-sm px-5 py-3 rounded-sm hover:bg-slate-800 transition-colors mb-10">
        <Download size={16} /> Scarica il documento PDF
      </a>

      {/* Editorial / legal notes */}
      <div className="rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-white/5 p-5 mb-10">
        <div className="flex items-center gap-2 mb-3"><FileText size={18} className="text-legend-red" /><span className="font-display font-bold uppercase">Note Legali e Informazioni Editoriali</span></div>
        <ul className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5">
          <li><strong>Registrazione testata:</strong> Tribunale di Rimini n. 5 del Registro della Stampa, in data 02/09/2026.</li>
          <li><strong>Editore e Proprietario:</strong> Legend Team S.R.L. Sportiva Dilettantistica</li>
          <li><strong>Sede legale:</strong> Strada Perticara 13, 47866 Sant'Agata Feltria (RN)</li>
          <li><strong>C.F. e P.IVA:</strong> 03858691201 &nbsp;|&nbsp; <strong>REA:</strong> RN-426201</li>
          <li><strong>Direttore Responsabile:</strong> Simone Massacesi</li>
          <li><strong>PEC:</strong> <a href={`mailto:${PEC}`} className="text-legend-red underline">{PEC}</a></li>
        </ul>
      </div>

      <Section title={<span className="inline-flex items-center gap-2"><Copyright size={20} className="text-legend-red" /> Copyright e Proprietà Intellettuale</span>}>
        <p>Tutti i contenuti pubblicati sul sito <strong>legendmagazine.it</strong> (inclusi testi, articoli, fotografie, marchi, loghi, video, grafica e la loro disposizione) sono di proprietà esclusiva di <strong>Legend Team S.R.L. S.D.F.</strong> o dei rispettivi autori e titolari, protetti dalle leggi italiane ed internazionali sul diritto d'autore (Legge n. 633/1941 e successive modifiche).</p>
        <p>È espressamente vietata la riproduzione, la distribuzione, la pubblicazione, la copia o la modifica di qualsiasi materiale senza preventiva autorizzazione scritta della società editrice. È consentita la condivisione di link diretti agli articoli nel rispetto del diritto di cronaca, a condizione che venga citata chiaramente la fonte "Legend Magazine".</p>
      </Section>

      <Section title={<span className="inline-flex items-center gap-2"><AlertTriangle size={20} className="text-legend-red" /> Esonero di Responsabilità (Disclaimer)</span>}>
        <p><strong>Contenuti informativi:</strong> Legend Team S.R.L. S.D.F. adotta la massima cura nella redazione e verifica dei contenuti pubblicati. Tuttavia, non si assume alcuna responsabilità per eventuali errori, inesattezze, omissioni o per danni derivanti dall'uso delle informazioni ivi contenute.</p>
        <p><strong>Link esterni:</strong> Il sito può contenere collegamenti a siti internet di terze parti (partner, federazioni, organizzatori di eventi). Tali siti non sono sotto il controllo di Legend Magazine, la quale declina ogni responsabilità in merito ai loro contenuti o alle politiche sulla privacy adottate.</p>
      </Section>

      <div className="mt-10 pt-8 border-t-2 border-legend-red">
        <h2 className="font-display font-black uppercase text-2xl md:text-3xl mb-1">Privacy & Cookie Policy</h2>
        <p className="text-slate-500 text-sm mb-6">Informativa ai sensi del GDPR (Regolamento UE 2016/679)</p>
      </div>

      <Section n="1" title="Titolare del Trattamento">
        <p>Il Titolare del trattamento dei dati raccolti tramite il sito è la <strong>Legend Team S.R.L. Sportiva Dilettantistica</strong>, con sede in Strada Perticara 13, 47866 Sant'Agata Feltria (RN), P.IVA 03858691201, in persona dell'Amministratrice Unica <strong>Rita Seminerio</strong>. PEC: <a href={`mailto:${PEC}`} className="text-legend-red underline">{PEC}</a>.</p>
      </Section>

      <Section n="2" title="Tipologia di Dati e Finalità">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Dati di navigazione:</strong> i sistemi informatici acquisiscono autonomamente alcuni dati di navigazione (indirizzi IP, log) necessari per il corretto funzionamento dei protocolli telematici.</li>
          <li><strong>Dati forniti volontariamente:</strong> l'invio di messaggi via e-mail o PEC e la compilazione di moduli sul sito comportano l'acquisizione dei dati di contatto necessari per gestire le richieste, i tesseramenti o le iscrizioni alle attività sportive.</li>
        </ul>
      </Section>

      <Section n="3" title="Base Giuridica e Conservazione">
        <p>Il trattamento si fonda sul consenso dell'utente, sull'esecuzione di obblighi contrattuali/associativi e sugli adempimenti di legge. I dati sono conservati per il tempo strettamente necessario a conseguire gli scopi per cui sono stati raccolti.</p>
      </Section>

      <Section n="4" title="Diritti degli Interessati">
        <p>Gli utenti possono esercitare in qualsiasi momento i diritti riconosciuti dal GDPR (accesso, rettifica, cancellazione, limitazione) inviando una comunicazione formale all'indirizzo PEC: <a href={`mailto:${PEC}`} className="text-legend-red underline">{PEC}</a>.</p>
      </Section>

      <Section n="5" title="Informativa sui Cookie">
        <p>Il sito utilizza cookie tecnici necessari al corretto funzionamento della piattaforma e cookie analitici di terze parti per finalità statistiche aggregate. L'utente può gestire o disattivare i cookie direttamente tramite le impostazioni del proprio browser o dal pannello qui sotto.</p>
        <div className="mt-4 rounded-sm border border-slate-200 dark:border-slate-800 p-5 bg-slate-50 dark:bg-white/5">
          <div className="flex items-center gap-2 mb-4"><Cookie size={18} className="text-legend-red" /><span className="font-display font-bold uppercase">Gestione preferenze cookie</span></div>
          {[
            { key: "necessari", label: "Cookie necessari", desc: "Indispensabili per il funzionamento del sito. Sempre attivi.", locked: true },
            { key: "statistici", label: "Cookie statistici", desc: "Ci aiutano a capire come viene utilizzato il sito." },
            { key: "marketing", label: "Cookie di marketing", desc: "Utilizzati per mostrare contenuti pubblicitari pertinenti." },
          ].map((c) => (
            <label key={c.key} className="flex items-start justify-between gap-4 py-3 border-t border-slate-200 dark:border-slate-800 first:border-t-0">
              <div>
                <p className="font-semibold text-sm">{c.label}</p>
                <p className="text-xs text-slate-500">{c.desc}</p>
              </div>
              <input
                type="checkbox"
                data-testid={`cookie-${c.key}`}
                disabled={c.locked}
                checked={prefs[c.key]}
                onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })}
                className="mt-1 w-5 h-5 accent-legend-red"
              />
            </label>
          ))}
          <button onClick={save} data-testid="cookie-save-btn" className="mt-4 w-full bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-5 py-3 rounded-sm transition-colors">
            Salva preferenze
          </button>
        </div>
      </Section>
    </div>
  );
}
